'use client';

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  readRequestContextCookie as readRequestContextCookieValue,
  REQUEST_CONTEXT_COOKIE_NAME,
  UNKNOWN_REQUEST_CONTEXT,
  type RequestContextSnapshot,
} from '@highereducation/request-context';

interface RequestContextBridgeValue {
  snapshot: RequestContextSnapshot;
}

const RequestContextBridgeContext = createContext<RequestContextBridgeValue>({
  snapshot: UNKNOWN_REQUEST_CONTEXT,
});

export { REQUEST_CONTEXT_COOKIE_NAME };

/**
 * Reads `rv_ctx` from a cookie header. The shared reader accepts the stored
 * value: percent-encoded JSON, or raw JSON that already starts with `{`.
 */
export function readRequestContextCookie(
  cookieHeader: string,
): RequestContextSnapshot {
  const prefix = `${REQUEST_CONTEXT_COOKIE_NAME}=`;
  const encodedValue = cookieHeader
    .split(';')
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(prefix))
    ?.slice(prefix.length);

  if (!encodedValue) return UNKNOWN_REQUEST_CONTEXT;

  return readRequestContextCookieValue(encodedValue);
}

export function useRequestContextBridge(): RequestContextBridgeValue {
  return useContext(RequestContextBridgeContext);
}

function sameRequestContext(
  left: RequestContextSnapshot,
  right: RequestContextSnapshot,
): boolean {
  return (
    left.partnerTripId === right.partnerTripId &&
    left.sessionState === right.sessionState &&
    left.experiment === right.experiment &&
    left.bot === right.bot &&
    left.consent === right.consent
  );
}

/**
 * Loads the visitor-facts snapshot from the browser cookie after mount.
 * The first render is always the unknown snapshot, so server HTML does not
 * depend on visitor facts.
 */
export function RequestContextBridge({ children }: { children: ReactNode }) {
  const [snapshot, setSnapshot] = useState<RequestContextSnapshot>(
    UNKNOWN_REQUEST_CONTEXT,
  );

  useEffect(() => {
    const next = readRequestContextCookie(document.cookie);
    document.documentElement.dataset.requestContextSnapshot =
      sameRequestContext(next, UNKNOWN_REQUEST_CONTEXT) ? 'unknown' : 'known';
    setSnapshot((current) =>
      sameRequestContext(current, next) ? current : next,
    );
  }, []);

  const value = useMemo(() => ({ snapshot }), [snapshot]);

  return (
    <RequestContextBridgeContext.Provider value={value}>
      {children}
    </RequestContextBridgeContext.Provider>
  );
}
